import React, { useEffect, useRef, useState } from "react";

import { NavLink, useNavigate } from "react-router-dom";

import Select from "react-select";
import { toast } from "react-toastify";

import { createPartner, uploadPartnerPdf } from "../Services/partnerService";

import { RoutesPaths, countryCodes } from "../Router/constant";

import {
  FiBox,
  FiCalendar,
  FiCheck,
  FiChevronDown,
  FiChevronRight,
  FiFileText,
  FiGlobe,
  FiHome,
  FiMail,
  FiMapPin,
  FiPhone,
  FiPlus,
  FiSearch,
  FiTruck,
  FiUploadCloud,
  FiUser,
  FiUsers,
  FiX,
} from "react-icons/fi";
import {
  MdOutlineAssignmentTurnedIn,
  MdOutlineBadge,
  MdOutlineBusiness,
  MdOutlinePictureAsPdf,
  MdOutlineShareLocation,
  MdOutlineWarehouse,
} from "react-icons/md";

export default function AddVendor() {
  const navigate = useNavigate();

  const countryRef = useRef(null);

  /* =========================================
     DEFAULT COUNTRY
  ========================================= */

  const getDefaultCountry = () =>
    countryCodes.find((country) => country.code === "AO") ||
    countryCodes.find((country) => country.dial_code === "+244") ||
    countryCodes[0];

  /* =========================================
     FORM STATE
  ========================================= */

  const [form, setForm] = useState({
    companyName: "",

    businessRegistrationNumber: "",

    serviceTypes: [],

    email: "",

    headquartersAddress: "",

    countryCode: "+244",

    countryIso: "AO",

    phone: "",

    contactPersonName: "",

    contactPersonTitle: "",

    website: "",

    numberOfEmployees: "",

    yearOfExperience: "",

    geographicCoverageAreas: [],

    warehousingCapacity: "",
  });

  /* =========================================
     FILES
  ========================================= */

  const [files, setFiles] = useState({
    commercialCertificate: null,

    operatingLicense: null,

    insuranceCertificate: null,
  });

  /* =========================================
     FLEET
  ========================================= */

  const [fleet, setFleet] = useState([
    {
      vehicleType: "",

      qty: "",

      capacity: "",
    },
  ]);

  /* =========================================
     SUBMIT LOADER
  ========================================= */

  const [saving, setSaving] = useState(false);

  /* =========================================
     COUNTRY
  ========================================= */

  const [isCountryOpen, setIsCountryOpen] = useState(false);

  const [countrySearch, setCountrySearch] = useState("");

  const [selectedCountry, setSelectedCountry] = useState(getDefaultCountry());

  /* =========================================
     ERRORS
  ========================================= */

  const [errors, setErrors] = useState({});

  /* =========================================
     SERVICE OPTIONS
  ========================================= */

  const serviceTypesOptions = [
    {
      value: "Air freight",
      label: "Air freight",
    },

    {
      value: "Sea freight",
      label: "Sea freight",
    },

    {
      value: "Rail freight",
      label: "Rail freight",
    },

    {
      value: "Trucking",
      label: "Trucking",
    },

    {
      value: "Warehouse",
      label: "Warehouse",
    },
  ];

  /* =========================================
     GEOGRAPHIC COVERAGE OPTIONS
  ========================================= */

  const geographicCoverageOptions = countryCodes
    .map((country) => {
      const name =
        country.name || country.country || country.label || country.code;

      return {
        value: name,
        label: name,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  /* =========================================
     REACT SELECT STYLE
  ========================================= */

  const customStyles = {
    control: (base) => ({
      ...base,

      border: "none",

      boxShadow: "none",

      background: "transparent",

      minHeight: "unset",
    }),

    multiValueLabel: (base) => ({
      ...base,

      fontSize: "11px",
    }),

    menuPortal: (base) => ({
      ...base,

      zIndex: 99999,
    }),
  };

  /* =========================================
     UPDATE FORM
  ========================================= */

  const update = (key, value) => {
    setForm((prev) => ({
      ...prev,

      [key]: value,
    }));

    setErrors((prev) => ({
      ...prev,

      [key]: "",
    }));
  };

  /* =========================================
     NORMAL INPUT CHANGE
  ========================================= */

  const handleChange = (event) => {
    const { name, value } = event.target;

    let finalValue = value;

    /* PHONE NUMERIC ONLY */

    if (name === "phone") {
      finalValue = value.replace(/\D/g, "");
    }

    /* TAX ID */

    if (name === "businessRegistrationNumber") {
      finalValue = value.replace(/[^a-zA-Z0-9]/g, "");
    }

    /* EMPLOYEE / EXPERIENCE */

    if (name === "numberOfEmployees" || name === "yearOfExperience") {
      finalValue = value.replace(/\D/g, "");
    }

    update(name, finalValue);
  };

  /* =========================================
     COUNTRY SEARCH
  ========================================= */

  const filteredCountries = countryCodes.filter((country) => {
    const search = String(countrySearch || "")
      .trim()
      .toLowerCase();

    if (!search) {
      return true;
    }

    const countryName = String(
      country.name || country.country || country.label || "",
    ).toLowerCase();

    const countryCode = String(country.code || "").toLowerCase();

    const dialCode = String(country.dial_code || "").toLowerCase();

    return (
      countryName.includes(search) ||
      countryCode.includes(search) ||
      dialCode.includes(search)
    );
  });

  /* =========================================
     COUNTRY SELECT
  ========================================= */

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);

    setForm((prev) => ({
      ...prev,

      countryCode: country.dial_code,

      countryIso: country.code,
    }));

    setIsCountryOpen(false);

    setCountrySearch("");
  };

  /* =========================================
     COUNTRY OUTSIDE CLICK
  ========================================= */

  useEffect(() => {
    const handleOutside = (event) => {
      if (countryRef.current && !countryRef.current.contains(event.target)) {
        setIsCountryOpen(false);

        setCountrySearch("");
      }
    };

    document.addEventListener("mousedown", handleOutside);

    return () => {
      document.removeEventListener("mousedown", handleOutside);
    };
  }, []);

  /* =========================================
     FLEET ADD
  ========================================= */

  const addCapacity = () => {
    setFleet((prev) => [
      ...prev,

      {
        vehicleType: "",

        qty: "",

        capacity: "",
      },
    ]);
  };

  /* =========================================
     FLEET REMOVE
  ========================================= */

  const removeCapacity = (index) => {
    if (fleet.length === 1) {
      return;
    }

    setFleet((prev) => prev.filter((_, i) => i !== index));
  };

  /* =========================================
     FLEET CHANGE
  ========================================= */

  const handleCapacityChange = (index, field, value) => {
    let finalValue = value;

    if (field === "qty" || field === "capacity") {
      finalValue = value.replace(/\D/g, "");
    }

    setFleet((prev) =>
      prev.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,

              [field]: finalValue,
            }
          : item,
      ),
    );
  };

  /* =========================================
     FILE CHANGE
  ========================================= */

  const handleFileChange = (event, field) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    /* PDF ONLY */

    if (file.type !== "application/pdf") {
      toast.error("Please upload PDF file only.");

      event.target.value = "";

      return;
    }

    setFiles((prev) => ({
      ...prev,

      [field]: file,
    }));

    /* CLEAR DOCUMENT ERROR */

    setErrors((prev) => ({
      ...prev,

      [field]: "",
    }));
  };

  /* =========================================
     REMOVE FILE
  ========================================= */

  const removeFile = (field) => {
    setFiles((prev) => ({
      ...prev,

      [field]: null,
    }));

    /*
      Do not show any required message
      underneath document upload.
    */

    setErrors((prev) => ({
      ...prev,

      [field]: "",
    }));
  };

  /* =========================================
     UPLOAD CARD
     (render helper, not a component, so it isn't remounted on every keystroke)
  ========================================= */

  const renderUploadCard = ({ title, field, required = false }) => {
    const inputId = `vendor-${field}`;

    const file = files[field];

    return (
      <div
        id={`field-${field}`}
        className={`qv-upload-card ${errors[field] ? "has-error" : ""}`}
      >
        <div className="qv-upload-head">
          <span className="qv-upload-icon">
            <MdOutlinePictureAsPdf />
          </span>
          <span className="qv-upload-title">
            {title}
            {required && <span className="qv-required"> *</span>}
          </span>
        </div>

        <div
          className={`qv-dropzone ${file ? "has-file" : ""}`}
          role="button"
          tabIndex={0}
          onClick={() => document.getElementById(inputId)?.click()}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              document.getElementById(inputId)?.click();
            }
          }}
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            const dropped = event.dataTransfer.files?.[0];
            if (dropped) {
              handleFileChange({ target: { files: [dropped], value: "" } }, field);
            }
          }}
        >
          {file ? (
            <>
              <MdOutlinePictureAsPdf className="qv-file-icon" />
              <span className="qv-file-name">{file.name}</span>
              <button
                type="button"
                className="qv-file-remove"
                aria-label={`Remove ${title}`}
                onClick={(event) => {
                  event.stopPropagation();

                  removeFile(field);
                }}
              >
                <FiX />
              </button>
            </>
          ) : (
            <>
              <FiUploadCloud className="qv-drop-icon" />
              <span>
                <strong>Click to upload</strong> or drag and drop
              </span>
              <small>PDF (Max size: 5MB)</small>
            </>
          )}
        </div>

        {/* HIDDEN INPUT */}

        <input
          id={inputId}
          type="file"
          accept="application/pdf"
          style={{
            display: "none",
          }}
          onChange={(event) => handleFileChange(event, field)}
        />
      </div>
    );
  };

  /* =========================================
     SCROLL TO FIRST REQUIRED ERROR
  ========================================= */

  const scrollToFirstError = (newErrors) => {
    /*
      This array also controls validation
      scroll priority.
    */

    const fieldOrder = [
      "companyName",

      "businessRegistrationNumber",

      "serviceTypes",

      "email",

      "headquartersAddress",

      "phone",

      "contactPersonName",

      "contactPersonTitle",

      "commercialCertificate",

      "operatingLicense",

      "insuranceCertificate",
    ];

    const firstErrorField = fieldOrder.find((field) => newErrors[field]);

    if (!firstErrorField) {
      return;
    }

    /*
      Wait for React error state
      to render first.
    */

    setTimeout(() => {
      const element = document.getElementById(`field-${firstErrorField}`);

      if (!element) {
        return;
      }

      element.scrollIntoView({
        behavior: "smooth",

        block: "center",
      });

      /*
        Focus only visible normal inputs.

        We do not focus hidden PDF input.
      */

      const input = element.querySelector('input:not([type="file"]), textarea');

      if (input && typeof input.focus === "function") {
        setTimeout(() => {
          input.focus({
            preventScroll: true,
          });
        }, 500);
      }
    }, 100);
  };

  /* =========================================
     VALIDATION
  ========================================= */

  const validateForm = () => {
    const newErrors = {};

    /* COMPANY NAME */

    if (!form.companyName.trim()) {
      newErrors.companyName = "Please enter company name.";
    }

    /* BUSINESS REGISTRATION */

    if (!form.businessRegistrationNumber.trim()) {
      newErrors.businessRegistrationNumber =
        "Please enter business registration number.";
    }

    /* SERVICE TYPES */

    if (!form.serviceTypes.length) {
      newErrors.serviceTypes = "Please select service type.";
    }

    /* EMAIL */

    if (!form.email.trim()) {
      newErrors.email = "Please enter email address.";
    }

    /* HEADQUARTERS */

    if (!form.headquartersAddress.trim()) {
      newErrors.headquartersAddress = "Please enter headquarters address.";
    }

    /* PHONE */

    if (!form.phone.trim()) {
      newErrors.phone = "Please enter phone number.";
    }

    /* CONTACT NAME */

    if (!form.contactPersonName.trim()) {
      newErrors.contactPersonName = "Please enter contact person name.";
    }

    /* CONTACT TITLE */

    if (!form.contactPersonTitle.trim()) {
      newErrors.contactPersonTitle = "Please enter contact person title.";
    }

    /* =========================================
       REQUIRED DOCUMENTS

       true only.
       No document error message shown.
    ========================================= */

    if (!files.commercialCertificate) {
      newErrors.commercialCertificate = true;
    }

    if (!files.operatingLicense) {
      newErrors.operatingLicense = true;
    }

    if (!files.insuranceCertificate) {
      newErrors.insuranceCertificate = true;
    }

    setErrors(newErrors);

    const isValid = Object.keys(newErrors).length === 0;

    if (!isValid) {
      scrollToFirstError(newErrors);
    }

    return isValid;
  };

  /* =========================================
     SUBMIT
  ========================================= */

  const submit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    /* =====================================
       FLEET FORMAT
    ===================================== */

    const formattedFleet = fleet
      .filter((item) => item.vehicleType.trim() || item.qty || item.capacity)
      .map((item) => ({
        vehicleType: item.vehicleType.trim(),

        qty: item.qty,

        capacity: item.capacity,
      }));

    /* =====================================
       PAYLOAD
    ===================================== */

    const payload = {
      companyName: form.companyName,

      businessRegistrationNumber: form.businessRegistrationNumber,

      /*
       * EXACT SERVICE FORMAT:
       *
       * [
       *   "Air freight",
       *   "Sea freight"
       * ]
       */

      serviceTypes: form.serviceTypes,

      email: form.email,

      headquartersAddress: {
        address: form.headquartersAddress,

        longitude: 0,

        latitude: 0,
      },

      countryCode: selectedCountry?.dial_code || "+244",

      phone: form.phone,

      contactPersonName: form.contactPersonName,

      contactPersonTitle: form.contactPersonTitle,

      website: form.website,

      numberOfEmployees: form.numberOfEmployees,

      yearOfExperience: form.yearOfExperience,

      geographicCoverageAreas: form.geographicCoverageAreas.join(", "),

      fleetCapacity: formattedFleet,

      warehousingCapacity: form.warehousingCapacity,

      commercialCertificate: "",

      operatingLicense: "",

      insuranceCertificate: "",
    };

    try {
      setSaving(true);

      /* =====================================
         UPLOAD DOCUMENTS FIRST
      ===================================== */

      for (const [key, file] of Object.entries(files)) {
        if (file) {
          const upload = await uploadPartnerPdf(file);

          payload[key] =
            upload?.data?.data?.id ||
            upload?.data?.data?._id ||
            upload?.data?.data?.fileId ||
            upload?.data?.data?.filename ||
            upload?.data?.filename;

          if (!payload[key]) {
            throw new Error(`Document upload failed: ${file.name}`);
          }
        }
      }

      /* =====================================
         CHECK FINAL PAYLOAD
      ===================================== */

      console.log("Create vendor payload:", payload);

      console.log("Service types payload:", payload.serviceTypes);

      /* =====================================
         CREATE VENDOR
      ===================================== */

      const response = await createPartner(payload);

      const message =
        response?.data?.data?.message ||
        response?.data?.message ||
        "Vendor created successfully.";

      if (response?.data?.status === false) {
        throw new Error(message);
      }

      toast.success(message);

      navigate(RoutesPaths.VENDOR);
    } catch (error) {
      console.error("Create vendor:", error);

      toast.error(
        error?.response?.data?.data?.message ||
          error?.response?.data?.message ||
          error?.message ||
          "Unable to create vendor.",
      );
    } finally {
      setSaving(false);
    }
  };

  /* =========================================
     FIELD HELPERS
  ========================================= */

  const renderField = ({ field, label, required, Icon, children, className = "" }) => (
    <div
      id={field ? `field-${field}` : undefined}
      className={`qv-field ${field && errors[field] ? "has-error" : ""} ${className}`}
    >
      <span className="qv-field-icon">
        <Icon />
      </span>
      <div className="qv-field-body">
        <label className="qv-label">
          {label}
          {required && <span className="qv-required"> *</span>}
        </label>
        {children}
        {field && errors[field] && <p className="m-0 qv-error">{errors[field]}</p>}
      </div>
    </div>
  );

  const renderTextField = ({ name, label, required, Icon, placeholder, type = "text", numeric }) =>
    renderField({
      field: required ? name : undefined,
      label,
      required,
      Icon,
      children: (
        <input
          type={type}
          name={name}
          className="qv-input"
          placeholder={placeholder}
          value={form[name]}
          onChange={handleChange}
          inputMode={numeric ? "numeric" : undefined}
        />
      ),
    });

  return (
    <div className="container-fluid qd-page qv-page">
      {/* Breadcrumb */}
      <nav className="qd-breadcrumb">
        <NavLink to={RoutesPaths.DASHBOARD} aria-label="Dashboard">
          <FiHome />
        </NavLink>
        <FiChevronRight />
        <NavLink to={RoutesPaths.VENDOR}>Vendors</NavLink>
        <FiChevronRight />
        <span className="qd-breadcrumb-current">Add vendor</span>
      </nav>

      {/* Hero */}
      <div
        className="qo-hero"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/img/LogisticsRoute.png)` }}
      >
        <h1 className="qd-title">Add vendor</h1>
        <p className="qd-subtitle">
          Fill in the vendor&apos;s information to create a new vendor in the system.
        </p>
      </div>

      <form onSubmit={submit}>
        {/* =====================================
            COMPANY INFORMATION
        ===================================== */}

        <section className="qd-card qv-section">
          <div className="qv-section-head">
            <span className="qv-section-icon">
              <MdOutlineBusiness />
            </span>
            <div>
              <h6>Company information</h6>
              <p>Provide the vendor&apos;s company and contact details.</p>
            </div>
          </div>

          <div className="qv-grid">
            {renderTextField({
              name: "companyName",
              label: "Company name",
              required: true,
              Icon: MdOutlineBusiness,
              placeholder: "Enter full legal business name",
            })}

            {renderTextField({
              name: "businessRegistrationNumber",
              label: "Business registration number / Tax ID",
              required: true,
              Icon: FiFileText,
              placeholder: "Enter business registration number",
            })}

            {renderField({
              field: "serviceTypes",
              label: "Service types",
              required: true,
              Icon: FiBox,
              children: (
                <Select
                  isMulti
                  options={serviceTypesOptions}
                  placeholder="Select service type"
                  styles={customStyles}
                  className="qv-select"
                  closeMenuOnSelect={false}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  value={serviceTypesOptions.filter((option) =>
                    form.serviceTypes.includes(option.value),
                  )}
                  onChange={(selected) => {
                    const values = selected ? selected.map((option) => option.value) : [];

                    update("serviceTypes", values);
                  }}
                />
              ),
            })}

            {renderTextField({
              name: "email",
              type: "email",
              label: "Email address",
              required: true,
              Icon: FiMail,
              placeholder: "Enter email address",
            })}

            {renderTextField({
              name: "headquartersAddress",
              label: "Headquarters address",
              required: true,
              Icon: FiMapPin,
              placeholder: "Enter headquarters address",
            })}

            {renderField({
              field: "phone",
              label: "Phone number",
              required: true,
              Icon: FiPhone,
              children: (
                <div className="qv-phone">
                  <div className="qo-country" ref={countryRef}>
                    <button
                      type="button"
                      className="qv-country-trigger"
                      onClick={() => {
                        setIsCountryOpen((prev) => !prev);

                        if (isCountryOpen) {
                          setCountrySearch("");
                        }
                      }}
                    >
                      <img
                        src={`https://flagcdn.com/24x18/${selectedCountry.code.toLowerCase()}.png`}
                        alt={selectedCountry.code}
                        className="qo-flag"
                      />
                      <span>
                        {selectedCountry.dial_code} ({selectedCountry.code})
                      </span>
                      <FiChevronDown className={`qo-chevron ${isCountryOpen ? "open" : ""}`} />
                    </button>

                    {isCountryOpen && (
                      <div className="qo-country-menu" onClick={(event) => event.stopPropagation()}>
                        <div className="qo-country-search">
                          <FiSearch />
                          <input
                            type="text"
                            value={countrySearch}
                            autoFocus
                            placeholder="Search country or code"
                            onChange={(event) => setCountrySearch(event.target.value)}
                          />
                          {countrySearch && (
                            <button type="button" onClick={() => setCountrySearch("")} aria-label="Clear">
                              <FiX />
                            </button>
                          )}
                        </div>

                        <div className="qo-country-list">
                          {filteredCountries.length > 0 ? (
                            filteredCountries.map((country) => {
                              const isSelected = selectedCountry?.code === country.code;

                              return (
                                <div
                                  key={`${country.code}-${country.dial_code}`}
                                  className={`qo-country-item ${isSelected ? "selected" : ""}`}
                                  onClick={() => handleCountrySelect(country)}
                                >
                                  <img
                                    src={`https://flagcdn.com/24x18/${country.code.toLowerCase()}.png`}
                                    alt={country.code}
                                    className="qo-flag"
                                  />
                                  <span className="qo-country-info">
                                    <span>
                                      {country.name || country.country || country.label || country.code}
                                    </span>
                                    <small>
                                      {country.dial_code} ({country.code})
                                    </small>
                                  </span>
                                  {isSelected && <FiCheck className="qo-accent" />}
                                </div>
                              );
                            })
                          ) : (
                            <div className="qo-country-empty">No country found</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <input
                    type="text"
                    name="phone"
                    className="qv-input"
                    placeholder="Enter phone number"
                    value={form.phone}
                    onChange={handleChange}
                    inputMode="numeric"
                  />
                </div>
              ),
            })}

            {renderTextField({
              name: "contactPersonName",
              label: "Contact person name",
              required: true,
              Icon: FiUser,
              placeholder: "Enter contact person name",
            })}

            {renderTextField({
              name: "contactPersonTitle",
              label: "Contact person title",
              required: true,
              Icon: MdOutlineBadge,
              placeholder: "Enter contact person title",
            })}

            {renderTextField({
              name: "website",
              label: "Website",
              Icon: FiGlobe,
              placeholder: "Enter website",
            })}

            {renderTextField({
              name: "numberOfEmployees",
              label: "Number of employees",
              Icon: FiUsers,
              placeholder: "Enter number of employees",
              numeric: true,
            })}

            {renderTextField({
              name: "yearOfExperience",
              label: "Years of experience",
              Icon: FiCalendar,
              placeholder: "Enter years of experience",
              numeric: true,
            })}

            {renderField({
              label: "Geographic coverage areas",
              Icon: MdOutlineShareLocation,
              children: (
                <Select
                  isMulti
                  placeholder="Select geographic coverage areas"
                  options={geographicCoverageOptions}
                  styles={customStyles}
                  className="qv-select"
                  closeMenuOnSelect={false}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  value={geographicCoverageOptions.filter((option) =>
                    form.geographicCoverageAreas.includes(option.value),
                  )}
                  onChange={(selected) => {
                    const values = selected ? selected.map((option) => option.value) : [];

                    update("geographicCoverageAreas", values);
                  }}
                />
              ),
            })}

            {renderField({
              label: "Fleet capacity",
              Icon: FiTruck,
              className: "qv-field-fleet",
              children: (
                <>
                  <button type="button" className="qv-add-btn" onClick={addCapacity}>
                    <FiPlus /> Add
                  </button>

                  {fleet.map((item, index) => (
                    <div className="qv-fleet-row" key={index}>
                      <input
                        type="text"
                        className="qv-input"
                        placeholder="Enter vehicle type"
                        aria-label="Vehicle type"
                        value={item.vehicleType}
                        onChange={(event) =>
                          handleCapacityChange(index, "vehicleType", event.target.value)
                        }
                      />

                      <input
                        type="text"
                        inputMode="numeric"
                        className="qv-input"
                        placeholder="Enter qty"
                        aria-label="Quantity"
                        value={item.qty}
                        onChange={(event) => handleCapacityChange(index, "qty", event.target.value)}
                      />

                      <input
                        type="text"
                        inputMode="numeric"
                        className="qv-input"
                        placeholder="Enter capacity"
                        aria-label="Capacity"
                        value={item.capacity}
                        onChange={(event) =>
                          handleCapacityChange(index, "capacity", event.target.value)
                        }
                      />

                      {fleet.length > 1 && (
                        <button
                          type="button"
                          className="qv-fleet-remove"
                          onClick={() => removeCapacity(index)}
                          aria-label="Remove vehicle"
                        >
                          <FiX />
                        </button>
                      )}
                    </div>
                  ))}
                </>
              ),
            })}

            {renderTextField({
              name: "warehousingCapacity",
              label: "Warehousing capacity",
              Icon: MdOutlineWarehouse,
              placeholder: "Enter warehousing capacity",
            })}
          </div>
        </section>

        {/* =====================================
            LICENSING AND COMPLIANCE
        ===================================== */}

        <section className="qd-card qv-section">
          <div className="qv-section-head">
            <span className="qv-section-icon">
              <MdOutlineAssignmentTurnedIn />
            </span>
            <div>
              <h6>Licensing and compliance</h6>
              <p>Upload vendor documents for verification.</p>
            </div>
          </div>

          <div className="qv-upload-grid">
            {renderUploadCard({
              title: "Commercial certificate",
              field: "commercialCertificate",
              required: true,
            })}

            {renderUploadCard({
              title: "Operating license",
              field: "operatingLicense",
              required: true,
            })}

            {renderUploadCard({
              title: "Insurance certificate",
              field: "insuranceCertificate",
              required: true,
            })}
          </div>
        </section>

        {/* =====================================
            ACTIONS
        ===================================== */}

        <div className="qv-actions">
          <NavLink to={RoutesPaths.VENDOR} className="qd-btn qv-cancel-btn">
            Cancel
          </NavLink>

          <button type="submit" className="qd-btn qd-btn-primary qv-submit" disabled={saving}>
            {saving ? (
              <>
                <span className="qo-spinner" /> Saving...
              </>
            ) : (
              "Create vendor"
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
